import { View, Text } from 'react-native'
import React from 'react'
import Button from '@ui/buttons/Button'
import Icon, { IconName } from '../Icon'
import { colors as c } from '@ui/colors'
import { GradientType } from '@ui/types'
import { hex } from '@ui/utils/hex'
import Border from '@ui/effects/Border'
import Gradient from '@ui/effects/Gradient'
import FormattedText from '@ui/typography/FormattedText'
import { ButtonProps } from '@ui/buttons/Button/types'

const borderRadius = 16
const borderWidth = 2

type Variant = 'neutral' | 'warning' | 'danger'

const iconBy: Record<Variant, IconName> = {
  neutral: 'alert-triangle',
  warning: 'alert-triangle',
  danger: 'alert-triangle',
}
const colorBack: Record<Variant, GradientType> = {
  neutral: [hex(c.neutral[500], 0.1), c.white25, c.white],
  warning: [hex(c.orange[500], 0.1), c.white25, c.white],
  danger: [hex(c.red[500], 0.1), c.white25, c.white],
}

const colorBorder: Record<Variant, string> = {
  neutral: hex(c.neutral[600], 1),
  warning: hex(c.orange[600], 1),
  danger: hex(c.red[600], 1),
}

const colorFront: Record<Variant, string> = {
  neutral: c.neutral[600],
  warning: c.orange[600],
  danger: c.red[600],
}

const colorTextStrong: Record<Variant, string> = {
  neutral: c.neutral[600],
  warning: c.orange[600],
  danger: c.red[600],
}

const colorTextNormal: Record<Variant, string> = {
  neutral: hex(c.neutral[600], 0.6),
  warning: hex(c.orange[600], 0.75),
  danger: hex(c.red[600], 0.75),
}

interface Props {
  text: string
  icon?: IconName
  variant?: Variant
  buttonProps?: ButtonProps
}

export default function AlertText({
  text,
  icon,
  variant = 'neutral',
  buttonProps,
}: Props) {
  return (
    <View className="relative inset-0 w-full">
      <Gradient
        colors={colorBack[variant]}
        start={[0, 0.5]}
        end={[1, 0.5]}
        style={{ borderRadius }}
      />
      <Border
        style={{
          borderRadius,
          borderWidth,
          borderColor: colorBorder[variant],
        }}
      />
      <Gradient
        colors={[c.white10, c.white50, c.white75, c.white75]}
        start={[0, 0.5]}
        end={[1, 0.5]}
        style={{
          borderRadius,
        }}
      />
      <View
        className="ga-8 flex flex-row items-center justify-between gap-8 border-transparent py-6 pl-6 pr-6"
        style={{ borderRadius, borderWidth }}
      >
        <View className="flex flex-1 flex-row items-start justify-start gap-4">
          <View className="flex w-8 flex-shrink-0 items-center justify-start py-1">
            <Icon
              name={icon || iconBy[variant]}
              color={colorFront[variant]}
              size={24}
            />
          </View>
          <View className="flex flex-1 flex-row items-start justify-start">
            <FormattedText
              text={text}
              variant="XS"
              styleBold={{ color: colorTextStrong[variant] }}
              styleNormal={{ color: colorTextNormal[variant] }}
              classNameBold="font-bold"
            />
          </View>
        </View>
        {buttonProps && (
          <View className="flex flex-shrink-0 flex-row items-center justify-end gap-4">
            <Button size="xs" variant="outlined" {...buttonProps} />
          </View>
        )}
      </View>
    </View>
  )
}
